import { create } from 'zustand';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import { conditionalDevtools } from '@/lib/storeMiddleware';
import { UNIFIED_ACCOUNT_ID, type StandardUnifiedRole } from '@/constants/mailbox';
import { buildUnifiedFolders, mapAccountFolders, normalizeText } from '@/lib/mailboxAggregations';
import { useAccountStore } from './accountStore';
import type { Folder } from '@/types/email';
import {
  fetchFolders,
  updateFolderPreferences,
  updateFolderNotificationSetting,
  markFolderAsRead as markFolderAsReadApi,
  renameFolder as renameFolderApi,
  type FolderPreferences,
  type FolderPreferencesInput,
  type FolderVisibility,
} from '@/api/folders';
import { useEmailStore } from './emailStore';
import { useSenderGroupingStore } from './senderGroupingStore';
import { setOptimisticFlags, clearOptimisticFlags } from './emailStateHelpers';
import { mailEvents } from '@/events/mailEvents';

interface FolderStoreState {
  folders: Folder[];
  foldersLoading: boolean;
  accountFolderCache: Record<string, Folder[]>;
  folderPreferencesByAccount: Record<string, FolderPreferences>;
  selectedFolderByAccount: Record<string, string>;
  savingFolderAccountIds: Set<string>;
  reloadTimer: number | null;
  notificationUpdatingMailboxIds: Set<string>;

  selectedFolderId: () => string | null;
  sidebarItems: () => Folder[];

  loadFolders: (accountId: string, options?: { background?: boolean }) => Promise<void>;
  selectFolder: (accountId: string, folderId: string | null) => void;
  updatePreferences: (accountId: string, input: FolderPreferencesInput) => Promise<void>;
  buildUnifiedFolders: () => Folder[];
  updateCountsFromEvent: (event: { accountId?: string | number | null; mailboxes: { id: string | number; unreadCount?: number | null; count?: number | null; totalCount?: number | null }[] }) => void;
  invalidateCacheForAccount: (accountId: string) => void;
  scheduleReload: (accountId: string, folderId: string) => void;
  cancelReload: () => void;
  clearFolders: () => void;
  updateMailboxNotification: (accountId: string, folderId: string, enabled: boolean) => Promise<void>;
  setFolderVisibility: (accountId: string, folderId: string, visibility: FolderVisibility) => Promise<void>;
  markFolderAsRead: (accountId: string, folderId: string) => Promise<void>;
  renameFolder: (accountId: string, folderId: string, newName: string) => Promise<void>;
}

function deriveFolderPreferences(accountId: string, folders: Folder[]): FolderPreferences {
  const roleMappings: FolderPreferences['roleMappings'] = {};
  const hiddenMailboxIds: string[] = [];

  folders.forEach((folder) => {
    if (folder.unifiedRole) {
      roleMappings[folder.unifiedRole as StandardUnifiedRole] = folder.id;
    }
    if (folder.hidden) {
      hiddenMailboxIds.push(folder.id);
    }
  });

  return {
    accountId,
    roleMappings,
    hiddenMailboxIds,
  };
}

function getDefaultFolderId(folders: Folder[]): string | null {
  if (!folders.length) {
    return null;
  }
  const inbox = folders.find((folder) => normalizeText(folder.role) === 'inbox' || normalizeText(folder.name) === 'inbox');
  return (inbox ?? folders[0]).id;
}

function buildNotificationKey(accountId: string, folderId: string): string {
  return `${accountId}:${folderId}`;
}

// E8: latest counts event per account, buffered while that account's folder
// cache is absent; re-applied right after loadFolders fills the cache.
const pendingCountsEvents = new Map<
  string,
  { accountId?: string | number | null; mailboxes: { id: string | number; unreadCount?: number | null; count?: number | null; totalCount?: number | null }[] }
>();

function flushPendingCounts(accountId: string) {
  const pending = pendingCountsEvents.get(accountId);
  if (!pending) {
    return;
  }
  pendingCountsEvents.delete(accountId);
  useFolderStore.getState().updateCountsFromEvent(pending);
}

export const useFolderStore = create<FolderStoreState>()(
  conditionalDevtools(
    subscribeWithSelector(
      persist(
        (set, get) => ({
          folders: [],
          foldersLoading: false,
          accountFolderCache: {},
          folderPreferencesByAccount: {},
          selectedFolderByAccount: {},
          savingFolderAccountIds: new Set(),
          reloadTimer: null,
          notificationUpdatingMailboxIds: new Set(),

          selectedFolderId: () => {
            const accountId = useAccountStore.getState().selectedAccountId;
            if (!accountId) {
              return null;
            }
            return get().selectedFolderByAccount[accountId] ?? null;
          },

          sidebarItems: () => {
            return get().folders;
          },

          loadFolders: async (accountId: string, options: { background?: boolean } = {}) => {
            // Background cache-warming (N14): never touch the global
            // foldersLoading — toggling it for every warmed account flickered
            // realtimeEnabled and churned the SSE connection on each app start.
            const background = Boolean(options.background);
            if (!accountId) {
              set({ folders: [], foldersLoading: false });
              return;
            }

            if (accountId === UNIFIED_ACCOUNT_ID) {
              const accountState = useAccountStore.getState();
              const actualAccounts = accountState.actualAccounts();
              if (!actualAccounts.length) {
                set({ folders: [], foldersLoading: false });
                get().selectFolder(UNIFIED_ACCOUNT_ID, null);
                return;
              }

              const currentCache = get().accountFolderCache;
              const missingAccounts = actualAccounts.filter((account) => !currentCache[account.id]);

              if (missingAccounts.length) {
                set({ foldersLoading: true });
                try {
                  const fetched = await Promise.all(
                    missingAccounts.map(async (account) => {
                      const data = await fetchFolders(account.id);
                      return { accountId: account.id, folders: mapAccountFolders(account.id, data) };
                    })
                  );
                  set((state) => {
                    const nextCache = { ...state.accountFolderCache };
                    const nextPrefs = { ...state.folderPreferencesByAccount };
                    fetched.forEach(({ accountId: id, folders }) => {
                      nextCache[id] = folders;
                      nextPrefs[id] = deriveFolderPreferences(id, folders);
                    });
                    return {
                      accountFolderCache: nextCache,
                      folderPreferencesByAccount: nextPrefs,
                    };
                  });
                  // Apply counts buffered while these caches were absent (E8)
                  fetched.forEach(({ accountId: id }) => flushPendingCounts(id));
                } finally {
                  set({ foldersLoading: false });
                }
              }

              const unified = buildUnifiedFolders(get().accountFolderCache, actualAccounts);
              set({ folders: unified });
              const selectedMap = get().selectedFolderByAccount;
              const currentSelection = selectedMap[UNIFIED_ACCOUNT_ID];
              const validSelection = currentSelection && unified.some((folder) => folder.id === currentSelection);
              const defaultId = validSelection ? currentSelection : getDefaultFolderId(unified);
              
              if (defaultId) {
                get().selectFolder(UNIFIED_ACCOUNT_ID, defaultId);
              }
              return;
            }

            // Only show loading indicator for initial loads (no cached data yet)
            // Avoid setting foldersLoading during refreshes since it disables SSE
            const isInitialLoad = !get().accountFolderCache[accountId];
            if (isInitialLoad && !background) {
              set({ foldersLoading: true });
            }
            try {
              const result = await fetchFolders(accountId);
              const enriched = mapAccountFolders(accountId, result);
              const accountState = useAccountStore.getState();
              const selectedAccountId = accountState.selectedAccountId;
              set((state) => {
                const nextCache = {
                  ...state.accountFolderCache,
                  [accountId]: enriched,
                };
                const nextPreferences = {
                  ...state.folderPreferencesByAccount,
                  [accountId]: deriveFolderPreferences(accountId, enriched),
                };
                let nextFolders = state.folders;
                if (selectedAccountId === accountId) {
                  nextFolders = enriched;
                } else if (selectedAccountId === UNIFIED_ACCOUNT_ID) {
                  nextFolders = buildUnifiedFolders(nextCache, accountState.actualAccounts());
                }
                return {
                  accountFolderCache: nextCache,
                  folderPreferencesByAccount: nextPreferences,
                  folders: nextFolders,
                  ...(background ? {} : { foldersLoading: false as const }),
                };
              });

              // Apply any counts event that arrived while the cache was absent (E8)
              flushPendingCounts(accountId);

              const selectedMap = get().selectedFolderByAccount;
              const currentSelection = selectedMap[accountId];
              const validSelection = currentSelection && enriched.some((folder) => folder.id === currentSelection);
              const folderIdToSelect = validSelection ? currentSelection : getDefaultFolderId(enriched);

              if (folderIdToSelect) {
                get().selectFolder(accountId, folderIdToSelect);
              }
            } catch (error) {
              if (isInitialLoad && !background) {
                set({ foldersLoading: false });
              }
              throw error;
            }
          },

          selectFolder: (accountId, folderId) => {
            if (!accountId) {
              return;
            }
            if (!folderId) {
              set((state) => {
                if (!(accountId in state.selectedFolderByAccount)) {
                  return state;
                }
                const next = { ...state.selectedFolderByAccount };
                delete next[accountId];
                return { selectedFolderByAccount: next };
              });
              return;
            }
            set((state) => ({
              selectedFolderByAccount: {
                ...state.selectedFolderByAccount,
                [accountId]: folderId,
              },
            }));
          },

          updatePreferences: async (accountId, input) => {
            set((state) => {
              const next = new Set(state.savingFolderAccountIds);
              next.add(accountId);
              return { savingFolderAccountIds: next };
            });

            try {
              const saved = await updateFolderPreferences(accountId, input);
              set((state) => ({
                folderPreferencesByAccount: {
                  ...state.folderPreferencesByAccount,
                  [accountId]: saved,
                },
              }));
              await get().loadFolders(accountId);
            } finally {
              set((state) => {
                const next = new Set(state.savingFolderAccountIds);
                next.delete(accountId);
                return { savingFolderAccountIds: next };
              });
            }
          },

          updateMailboxNotification: async (accountId, folderId, enabled) => {
            if (!accountId || accountId === UNIFIED_ACCOUNT_ID || !folderId) {
              return;
            }
            const key = buildNotificationKey(accountId, folderId);
            set((state) => {
              const next = new Set(state.notificationUpdatingMailboxIds);
              next.add(key);
              return { notificationUpdatingMailboxIds: next };
            });
            try {
              const result = await updateFolderNotificationSetting(accountId, folderId, enabled);
              set((state) => {
                const cached = state.accountFolderCache[accountId];
                if (!cached?.length) {
                  return state;
                }
                let changed = false;
                const nextCacheEntry = cached.map((folder) => {
                  if (folder.id !== result.mailboxId) {
                    return folder;
                  }
                  changed = true;
                  return {
                    ...folder,
                    notificationsEnabled: result.notificationsEnabled,
                    notificationsInherited: result.notificationsInherited,
                  };
                });
                if (!changed) {
                  return state;
                }
                const nextCache = {
                  ...state.accountFolderCache,
                  [accountId]: nextCacheEntry,
                };
                const accountState = useAccountStore.getState();
                const selectedAccountId = accountState.selectedAccountId;
                let nextFolders = state.folders;
                if (selectedAccountId === accountId) {
                  nextFolders = nextCacheEntry;
                } else if (selectedAccountId === UNIFIED_ACCOUNT_ID) {
                  nextFolders = buildUnifiedFolders(nextCache, accountState.actualAccounts());
                }
                return {
                  accountFolderCache: nextCache,
                  folders: nextFolders,
                };
              });
            } catch (error) {
              console.error('Failed to update mailbox notifications', error);
              throw error;
            } finally {
              set((state) => {
                const next = new Set(state.notificationUpdatingMailboxIds);
                next.delete(key);
                return { notificationUpdatingMailboxIds: next };
              });
            }
          },

          buildUnifiedFolders: () => {
            const accountState = useAccountStore.getState();
            const actualAccounts = accountState.actualAccounts();
            return buildUnifiedFolders(get().accountFolderCache, actualAccounts);
          },

          updateCountsFromEvent: (event) => {
            const accountId = String(event.accountId ?? '').trim();
            if (!accountId) {
              return;
            }
            // The account's folder cache may not be loaded yet (race with a
            // mailbox.list.changed reload, or an account never visited).
            // BUFFER the latest counts event instead of dropping it, load the
            // folders, and re-apply once they arrive (E8). One load per
            // account at a time (N24): a burst of counts events during the
            // cache-absent window otherwise spawned a fetch each.
            if (!get().accountFolderCache[accountId]?.length) {
              const alreadyLoading = pendingCountsEvents.has(accountId);
              pendingCountsEvents.set(accountId, event);
              if (!alreadyLoading) {
                void get()
                  .loadFolders(accountId, { background: true })
                  .catch(() => undefined)
                  .finally(() => {
                    // A failed load must not leave the buffer wedged — the
                    // next counts event retries.
                    if (!get().accountFolderCache[accountId]?.length) {
                      pendingCountsEvents.delete(accountId);
                    }
                  });
              }
              return;
            }
            const updates = new Map<string, { unread: number; total: number }>();
            event.mailboxes?.forEach((mailbox) => {
              updates.set(String(mailbox.id), {
                unread: Number(mailbox.unreadCount ?? mailbox.count ?? 0),
                total: Number(mailbox.totalCount ?? 0),
              });
            });

            set((state) => {
              const cached = state.accountFolderCache[accountId];
              if (!cached?.length || !updates.size) {
                return state;
              }
              let changed = false;
              const newCache = cached.map((folder) => {
                const value = updates.get(folder.id);
                if (!value) {
                  return folder;
                }
                if (folder.unreadCount === value.unread && folder.count === value.unread && folder.totalCount === value.total) {
                  return folder;
                }
                changed = true;
                return { ...folder, unreadCount: value.unread, count: value.unread, totalCount: value.total };
              });
              if (!changed) {
                return state;
              }
              const accountState = useAccountStore.getState();
              const selectedAccountId = accountState.selectedAccountId;
              const nextCache = {
                ...state.accountFolderCache,
                [accountId]: newCache,
              };
              let nextFolders = state.folders;
              if (selectedAccountId === accountId) {
                nextFolders = newCache;
              } else if (selectedAccountId === UNIFIED_ACCOUNT_ID) {
                nextFolders = buildUnifiedFolders(nextCache, accountState.actualAccounts());
              }
              return {
                accountFolderCache: nextCache,
                folders: nextFolders,
              };
            });
          },

          invalidateCacheForAccount: (accountId) => {
            set((state) => {
              if (!(accountId in state.accountFolderCache)) {
                return state;
              }
              const next = { ...state.accountFolderCache };
              delete next[accountId];
              return { accountFolderCache: next };
            });
          },

          scheduleReload: (accountId, folderId) => {
            if (typeof window === 'undefined') {
              return;
            }
            void folderId;
            const { reloadTimer } = get();
            if (reloadTimer !== null) {
              window.clearTimeout(reloadTimer);
            }
            const timer = window.setTimeout(() => {
              get().loadFolders(accountId).catch(() => undefined);
              set({ reloadTimer: null });
            }, 300);
            set({ reloadTimer: timer });
          },

          cancelReload: () => {
            if (typeof window === 'undefined') {
              return;
            }
            const { reloadTimer } = get();
            if (reloadTimer !== null) {
              window.clearTimeout(reloadTimer);
              set({ reloadTimer: null });
            }
          },

          clearFolders: () => {
            set({ folders: [] });
          },

          setFolderVisibility: async (accountId, folderId, visibility) => {
            if (!accountId || accountId === UNIFIED_ACCOUNT_ID || !folderId) {
              return;
            }
            const prefs = get().folderPreferencesByAccount[accountId];
            if (!prefs) {
              return;
            }

            // Update mailboxVisibility with new visibility state
            const currentVisibility = { ...(prefs.mailboxVisibility || {}) };

            // Default visibility is 'always', so only store non-default values
            if (visibility === 'always') {
              delete currentVisibility[folderId];
            } else {
              currentVisibility[folderId] = visibility;
            }

            await get().updatePreferences(accountId, {
              roleMappings: prefs.roleMappings,
              mailboxVisibility: currentVisibility,
            });
          },

          markFolderAsRead: async (accountId, folderId) => {
            if (!accountId || !folderId) {
              return;
            }

            // Optimistically mark the currently-loaded rows belonging to this
            // folder read and pin the dirty overlay, so visible bold rows clear
            // immediately and a concurrent reset/echo can't revert them before the
            // server confirms (mirrors the bulk mark-read path). Marking a folder
            // that isn't the displayed one simply matches no loaded rows.
            const emailStore = useEmailStore.getState();
            const senderStore = useSenderGroupingStore.getState();
            const affectedIds = emailStore.emails
              .filter(
                (email) =>
                  !email.read &&
                  email.folderId === folderId &&
                  (accountId === UNIFIED_ACCOUNT_ID || email.accountId === accountId)
              )
              .map((email) => email.id);

            affectedIds.forEach((id) => {
              emailStore.updateEmailInPlace(id, { read: true });
              setOptimisticFlags(id, { read: true });
              senderStore.updateDigestEmail(id, { read: true });
            });

            // Optimistically zero the folder's unread badge (count mirrors
            // unreadCount everywhere counts flow — see updateCountsFromEvent).
            // The success-path loadFolders confirms; an interim stale counts
            // event can transiently re-assert the old badge, which the post-op
            // counts/reload overwrite.
            set((state) => {
              const cached = state.accountFolderCache[accountId];
              if (!cached?.length) {
                return state;
              }
              let changed = false;
              const nextEntry = cached.map((folder) => {
                if (folder.id !== folderId || (folder.unreadCount === 0 && folder.count === 0)) {
                  return folder;
                }
                changed = true;
                return { ...folder, unreadCount: 0, count: 0 };
              });
              if (!changed) {
                return state;
              }
              const nextCache = {
                ...state.accountFolderCache,
                [accountId]: nextEntry,
              };
              const accountState = useAccountStore.getState();
              const selectedAccountId = accountState.selectedAccountId;
              let nextFolders = state.folders;
              if (selectedAccountId === accountId) {
                nextFolders = nextEntry;
              } else if (selectedAccountId === UNIFIED_ACCOUNT_ID) {
                nextFolders = buildUnifiedFolders(nextCache, accountState.actualAccounts());
              }
              return {
                accountFolderCache: nextCache,
                folders: nextFolders,
              };
            });

            try {
              await markFolderAsReadApi(accountId, folderId);
              // Reload folders to get updated unread counts
              await get().loadFolders(accountId);
            } catch (error) {
              // Roll back: drop the overlay and re-fetch the active list so server
              // truth (the still-unread rows) flows back, mirroring the bulk path.
              // Digest copies were all unread (the filter), so read: false is exact.
              affectedIds.forEach((id) => {
                clearOptimisticFlags(id);
                senderStore.updateDigestEmail(id, { read: false });
              });
              // Badge truth: the op may have partially applied server-side, so
              // re-fetch counts instead of restoring the prior number.
              await get().loadFolders(accountId);
              if (affectedIds.length > 0) {
                const activeFolderId = get().selectedFolderId();
                const activeAccountId = useAccountStore.getState().selectedAccountId;
                if (activeAccountId && activeFolderId) {
                  // Route through the reload event so this rollback respects list
                  // ownership (sender group / triage) instead of clobbering it
                  // with a mailbox list (L1/E6).
                  mailEvents.emit('mail:mailbox:reload', {
                    accountId: activeAccountId,
                    folderId: activeFolderId,
                    options: { reset: true },
                  });
                }
              }
              throw error;
            }
          },

          renameFolder: async (accountId, folderId, newName) => {
            if (!accountId || accountId === UNIFIED_ACCOUNT_ID || !folderId || !newName?.trim()) {
              return;
            }
            await renameFolderApi(accountId, folderId, newName.trim());
            // Reload folders to get updated name
            await get().loadFolders(accountId);
          },
        }),
        {
          name: 'folder-storage',
          partialize: (state) => ({
            selectedFolderByAccount: state.selectedFolderByAccount,
          }),
        }
      )
    ),
    { name: 'FolderStore' }
  )
);
